import { useToastContext } from "@ttc3k/trekker";
import {
  EnumOwnerType,
  EnumTagEntityTypes,
  TagManyDocument,
  useTagCreateMutation,
  useTagUpdateMutation,
} from "gql/generated";
import { useEffect, useState } from "react";
import {
  createMultilingual,
  getMultilingualString,
  updateMultilingual,
} from "shared/utils";
import { CreateEditBackOfficeTagModalProps } from "./CreateEditBackOfficeTagModal";
import { useTranslation } from "react-i18next";

type useCreateEditBackOfficeTagsProps = Omit<
  CreateEditBackOfficeTagModalProps,
  "isOpen"
>;

export const useCreateEditBackOfficeTags = ({
  tag,
  onClose,
  organizationId,
  tagGroupId,
}: useCreateEditBackOfficeTagsProps) => {
  const [tagName, setTagName] = useState(
    tag ? getMultilingualString(tag.name) : "",
  );

  const { t } = useTranslation();
  const { toastFactory } = useToastContext();

  const [updateTag] = useTagUpdateMutation({
    refetchQueries: [TagManyDocument],
  });
  const [createTag] = useTagCreateMutation({
    refetchQueries: [TagManyDocument],
  });
  const isEditingTag = !!tag;

  const handleClose = () => {
    setTagName("");
    onClose();
  };

  const handleSaveTag = async () => {
    try {
      if (isEditingTag) {
        await updateTag({
          variables: {
            id: tag._id,
            record: { name: updateMultilingual(tag.name, tagName) },
          },
        });
      } else {
        await createTag({
          variables: {
            record: {
              // BACK_OFFICE tags can be used for both operator and event tagging
              entityTypes: [
                EnumTagEntityTypes.Operator,
                EnumTagEntityTypes.Event,
              ],
              name: createMultilingual(tagName),
              owner: {
                entityId: organizationId,
                type: EnumOwnerType.Organization,
              },
              tagGroup: tagGroupId,
            },
          },
        });
      }

      toastFactory.create({
        title: t("core:SUCCESS"),
        description: t(
          `directory:ORGANIZATION.BACK_OFFICE.TAGS.${isEditingTag ? "UPDATED" : "CREATED"}.SUCCESS`,
          {
            tagName,
          },
        ),
      });
    } catch {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t(
          `directory:ORGANIZATION.BACK_OFFICE.TAGS.${isEditingTag ? "UPDATED" : "CREATED"}.ERROR`,
        ),
      });
    } finally {
      handleClose();
    }
  };

  useEffect(() => {
    setTagName(tag ? getMultilingualString(tag.name) : "");
  }, [tag]);

  return {
    tagName,
    setTagName,
    onCloseModal: handleClose,
    onSaveTag: handleSaveTag,
  };
};
